import http from "../../../http-common.js";
import jwt_decode from "jwt-decode";

let timer;

const AUTH_LOCAL_KEYS = [
  "token", "id", "username", "name", "role", "vendor",
  "payInStatus", "payOutStatus",
  // Per-branch manual-approve flag (JWT claim added 03062026).
  "canApprovePayinManually",
  // Per-vendor self-serve client management (JWT claim added 18062026).
  // Also gates /admin-report — the same flag governs both vendor
  // opt-in surfaces so ops has one toggle to grant "vendor self-serve".
  "canManageClients",
  // Partial-payout branch settings (JWT claims added 20092026). UI
  // affordance only — server re-checks fresh on pickup/approve.
  "canPickupPartialPayout",
  "canPartialApprovePayout",
  // Global toggle (JWT claim 02112026): show the Dashboard to vendors.
  "vendorDashboardEnabled",
  "isSupporting",
  // Supporting-user effective permissions (JSON array) — cached so a refresh
  // restores menu/route gating synchronously before the async re-fetch.
  "permissions",
  "exp", "iat", "tokenExpiration",
];

// Wipe every auth-related localStorage key plus the auth cookie. Called
// from tryLogin (when the timer says we're stale) and logout (always,
// regardless of whether the backend POST succeeded).
function clearAllAuthLocalStorage() {
  for (const k of AUTH_LOCAL_KEYS) localStorage.removeItem(k);
  try {
    document.cookie = "auth=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  } catch (e) { /* ignore */ }
}

export default {
  async login(context, payload) {
    // payload may include `code` for the 2FA second step.
    const data = JSON.stringify({
      username: payload.username,
      password: payload.password,
      ...(payload.code ? { code: payload.code } : {}),
      ...(payload.rememberMe ? { rememberMe: true } : {}),
    });

    let response;
    try {
      response = await http.post("/login", data);
    } catch (axiosErr) {
      // Invalid code on the second step comes back as 401 + totpRequired:true.
      // Surface the flag so the UI keeps the 2FA prompt open.
      if (axiosErr && axiosErr.response && axiosErr.response.data && axiosErr.response.data.totpRequired) {
        const err = new Error(axiosErr.response.data.message || "Invalid two-factor code.");
        err.totpRequired = true;
        err.totpInvalid = true;
        throw err;
      }
      throw axiosErr;
    }

    // 2FA step-up: server accepts the password but needs a 6-digit code.
    // Surface as a throwable error the UI can detect and prompt for code.
    if (response.status === 200 && response.data && response.data.totpRequired) {
      const err = new Error(response.data.message || "Two-factor code required.");
      err.totpRequired = true;
      throw err;
    }

    if (response.status == 200) {
      const responseData = response.data;

      const decoded = jwt_decode(responseData.token);

      const id = decoded.id;
      const username = decoded.username;
      const role = decoded.role;
      const vendor = decoded.vendor;
      const payInStatus = decoded.payInStatus;
      const payOutStatus = decoded.payOutStatus;
      const canApprovePayinManually = decoded.canApprovePayinManually === true;
      const canManageClients = decoded.canManageClients === true;
      // Partial-payout settings (20092026). pickup defaults OFF;
      // partial-approve defaults ON when the claim is absent (older token).
      const canPickupPartialPayout = decoded.canPickupPartialPayout === true;
      const canPartialApprovePayout = decoded.canPartialApprovePayout !== false;
      // Global toggle (02112026): vendors see the Dashboard when on.
      const vendorDashboardEnabled = decoded.vendorDashboardEnabled === true;
      const isSupporting = decoded.isSupporting === true;
      const exp = decoded.exp;
      const iat = decoded.iat;

      // Client-side expiry MUST track the real JWT lifetime, not a fixed
      // window. Previously hardcoded to 30 min (1800000ms) while the JWT
      // lives 24h (or 7d with "remember me") — so tryLogin() wiped auth and
      // bounced to /login on the first refresh after 30 min even though the
      // backend cookie/JWT was still valid. decoded.exp is in SECONDS.
      const expirationDate = Number(exp) * 1000;
      localStorage.setItem("tokenExpiration", expirationDate);

      localStorage.setItem("token", responseData.token);
      localStorage.setItem("id", id);
      localStorage.setItem("username", username);
      localStorage.setItem("role", role);
      localStorage.setItem("vendor", vendor);
      localStorage.setItem("payInStatus", payInStatus);
      localStorage.setItem("payOutStatus", payOutStatus);
      localStorage.setItem("canApprovePayinManually", canApprovePayinManually ? "1" : "0");
      localStorage.setItem("canManageClients", canManageClients ? "1" : "0");
      localStorage.setItem("canPickupPartialPayout", canPickupPartialPayout ? "1" : "0");
      localStorage.setItem("canPartialApprovePayout", canPartialApprovePayout ? "1" : "0");
      localStorage.setItem("vendorDashboardEnabled", vendorDashboardEnabled ? "1" : "0");
      localStorage.setItem("isSupporting", isSupporting ? "1" : "0");
      localStorage.setItem("exp", exp);
      localStorage.setItem("iat", iat);
      // timer = setTimeout(function() {
      //   context.dispatch("autoLogout");
      // }, expiresIn);
      context.commit("setUser", {
        token: responseData.token,
        id: id,
        username: username,
        role: role,
        vendor: vendor,
        payInStatus: payInStatus,
        payOutStatus: payOutStatus,
        canApprovePayinManually,
        canManageClients,
        canPickupPartialPayout,
        canPartialApprovePayout,
        isSupporting,
        exp: exp,
        iat: iat,
      });
      // Supporting users: load effective permissions so the sidebar + router
      // gate correctly on the very first navigation after login. Main users
      // are never permission-gated (the action no-ops for them).
      if (isSupporting) {
        await context.dispatch("loadEffectivePermissions");
        // Suppress the "no access" toast on the first post-login redirect.
        context.commit("setJustLoggedIn", true);
      }
    } else {
      const error = new Error("Failed to authenticate. Check your login data.");
      throw error;
    }
  },

  async clientLogin(context, payload) {
    const data = JSON.stringify({
      username: payload.username,
      password: payload.password,
      ...(payload.code ? { code: payload.code } : {}),
      ...(payload.rememberMe ? { rememberMe: true } : {}),
    });

    let response;
    try {
      response = await http.post("/client-login", data);
    } catch (axiosErr) {
      if (axiosErr && axiosErr.response && axiosErr.response.data && axiosErr.response.data.totpRequired) {
        const err = new Error(axiosErr.response.data.message || "Invalid two-factor code.");
        err.totpRequired = true;
        err.totpInvalid = true;
        throw err;
      }
      throw axiosErr;
    }

    if (response.status === 200 && response.data && response.data.totpRequired) {
      const err = new Error(response.data.message || "Two-factor code required.");
      err.totpRequired = true;
      throw err;
    }

    if (response.status == 200) {
      const responseData = response.data;

      const decoded = jwt_decode(responseData.token);

      const id = decoded.id;
      const username = decoded.username;
      const name = decoded.name || null;
      const role = decoded.role;
      const vendor = decoded.vendor;
      const payInStatus = decoded.payInStatus;
      const payOutStatus = decoded.payOutStatus;
      const canApprovePayinManually = decoded.canApprovePayinManually === true;
      const canManageClients = decoded.canManageClients === true;
      // Partial-payout settings (20092026). pickup defaults OFF;
      // partial-approve defaults ON when the claim is absent (older token).
      const canPickupPartialPayout = decoded.canPickupPartialPayout === true;
      const canPartialApprovePayout = decoded.canPartialApprovePayout !== false;
      // Global toggle (02112026): vendors see the Dashboard when on.
      const vendorDashboardEnabled = decoded.vendorDashboardEnabled === true;
      const isSupporting = decoded.isSupporting === true;
      const exp = decoded.exp;
      const iat = decoded.iat;

      // Client-side expiry MUST track the real JWT lifetime, not a fixed
      // window. Previously hardcoded to 30 min (1800000ms) while the JWT
      // lives 24h (or 7d with "remember me") — so tryLogin() wiped auth and
      // bounced to /login on the first refresh after 30 min even though the
      // backend cookie/JWT was still valid. decoded.exp is in SECONDS.
      const expirationDate = Number(exp) * 1000;
      localStorage.setItem("tokenExpiration", expirationDate);

      localStorage.setItem("token", responseData.token);
      localStorage.setItem("id", id);
      localStorage.setItem("username", username);
      if (name) localStorage.setItem("name", name);
      localStorage.setItem("role", role);
      localStorage.setItem("vendor", vendor);
      localStorage.setItem("payInStatus", payInStatus);
      localStorage.setItem("payOutStatus", payOutStatus);
      localStorage.setItem("canApprovePayinManually", canApprovePayinManually ? "1" : "0");
      localStorage.setItem("canManageClients", canManageClients ? "1" : "0");
      localStorage.setItem("canPickupPartialPayout", canPickupPartialPayout ? "1" : "0");
      localStorage.setItem("canPartialApprovePayout", canPartialApprovePayout ? "1" : "0");
      localStorage.setItem("vendorDashboardEnabled", vendorDashboardEnabled ? "1" : "0");
      localStorage.setItem("isSupporting", isSupporting ? "1" : "0");
      localStorage.setItem("exp", exp);
      localStorage.setItem("iat", iat);
      // timer = setTimeout(function() {
      //   context.dispatch("autoLogout");
      // }, expiresIn);
      context.commit("setUser", {
        token: responseData.token,
        id: id,
        username: username,
        name: name,
        role: role,
        vendor: vendor,
        payInStatus: payInStatus,
        payOutStatus: payOutStatus,
        canApprovePayinManually,
        canManageClients,
        canPickupPartialPayout,
        canPartialApprovePayout,
        isSupporting,
        exp: exp,
        iat: iat,
      });
      if (isSupporting) {
        await context.dispatch("loadEffectivePermissions");
        // Suppress the "no access" toast on the first post-login redirect.
        context.commit("setJustLoggedIn", true);
      }
    } else {
      const error = new Error("Failed to authenticate. Check your login data.");
      throw error;
    }
  },

  // Fetch (and cache) the caller's effective permissions. Only meaningful for
  // supporting users; main users are never permission-gated on the client, so
  // we clear instead of fetching. Fail-closed on error (empty set → only
  // self/account reachable) — the backend remains the real authority.
  async loadEffectivePermissions(context) {
    if (!context.getters.isSupporting) {
      context.commit("setPermissions", []);
      localStorage.removeItem("permissions");
      return;
    }
    try {
      const r = await http.get("/supported-users/effective-permissions");
      const perms = r.data && Array.isArray(r.data.permissions) ? r.data.permissions : [];
      context.commit("setPermissions", perms);
      localStorage.setItem("permissions", JSON.stringify(perms));
    } catch (e) {
      context.commit("setPermissions", []);
    }
  },

  tryLogin(context) {
    const token = localStorage.getItem("token");
    const id = localStorage.getItem("id");
    const username = localStorage.getItem("username");
    const name = localStorage.getItem("name");
    const role = localStorage.getItem("role");
    const vendor = localStorage.getItem("vendor");
    const payInStatus = localStorage.getItem("payInStatus");
    const payOutStatus = localStorage.getItem("payOutStatus");
    const canApprovePayinManually = localStorage.getItem("canApprovePayinManually") === "1";
    const canManageClients = localStorage.getItem("canManageClients") === "1";
    // Partial-payout settings (20092026). partial-approve defaults ON when
    // the key is absent (session predating this feature) — mirrors the
    // decode default so a refreshed old session isn't wrongly restricted.
    const canPickupPartialPayout = localStorage.getItem("canPickupPartialPayout") === "1";
    const canPartialApprovePayout = localStorage.getItem("canPartialApprovePayout") !== "0";
    const isSupporting = localStorage.getItem("isSupporting") === "1";
    const exp = localStorage.getItem("exp");
    const iat = localStorage.getItem("iat");

    const tokenExpiration = localStorage.getItem("tokenExpiration");
    const expiresIn = +tokenExpiration - new Date().getTime();

    // Stale token: previously this returned silently, leaving stale data
    // in localStorage. The backend cookie is also typically gone or
    // expired by now, so any subsequent API call would 401. Actively
    // wipe local auth so the next page render redirects to /login
    // cleanly via the router beforeEach guard.
    if (!tokenExpiration || expiresIn < 0) {
      clearAllAuthLocalStorage();
      return;
    }

    if (token && username) {
      context.commit("setUser", {
        token: token,
        id: id ? Number(id) : null,
        username: username,
        name: name,
        role: role,
        vendor: vendor,
        payInStatus: payInStatus,
        payOutStatus: payOutStatus,
        canApprovePayinManually,
        canManageClients,
        canPickupPartialPayout,
        canPartialApprovePayout,
        isSupporting,
        exp: exp,
        iat: iat,
      });
      // Restore cached permissions synchronously so the router guard gates
      // correctly on this same bootstrap, then refresh async (fire-and-forget)
      // in case grants changed server-side.
      if (isSupporting) {
        try {
          const cached = JSON.parse(localStorage.getItem("permissions") || "[]");
          context.commit("setPermissions", Array.isArray(cached) ? cached : []);
        } catch (_) {
          context.commit("setPermissions", []);
        }
        context.dispatch("loadEffectivePermissions");
      } else {
        context.commit("setPermissions", []);
      }
    }
  },

  async logout(context) {
    // Best-effort backend call. If the token is expired, the server returns
    // 401 and axios throws — that's fine, we still want to flush local state
    // so the user can recover. Previously we gated all the cleanup on a 200
    // response, which meant clicking Logout on an expired session did
    // nothing visible.
    try {
      await http.post("/logout");
    } catch (err) {
      // Intentionally swallow — local cleanup happens regardless.
    }

    clearAllAuthLocalStorage();
    clearTimeout(timer);

    context.commit("setUser", {
      token: null,
      id: null,
      username: null,
      role: null,
      vendor: null,
      payInStatus: null,
      payOutStatus: null,
      canApprovePayinManually: false,
      canManageClients: false,
      isSupporting: false,
      exp: null,
      iat: null,
    });
    context.commit("setPermissions", []);
    context.commit("setJustLoggedIn", false);
    context.commit("setAccessNotice", null);
  },

  autoLogout(context) {
    context.dispatch("logout");
    context.commit("setAutoLogout");
  },
};
